import { Injectable } from '@angular/core';
import {
  ASSET_ID_INVALID_CHARS_PATTERN,
  ASSET_ID_PATTERN_ERROR_MESSAGE,
  DEVICE_FIELD_LIMITS,
  TEXT_LIMIT_EXCEEDED_ERROR_MESSAGE,
  type DeviceFieldName,
} from '@cdw-ae/common-data';
import { UpdateType } from '../models/field-update-config';

/**
 * Validation result for a field value
 */
export interface ValidationResult {
  valid: boolean;
  errorMessage?: string;
  /** Array of all error messages when multiple validation rules fail */
  errorMessages?: string[];
  /** For Append/Prepend validation failures, indicates how many devices would exceed the limit */
  affectedDeviceCount?: number;
}

/**
 * Device metadata values needed for Append/Prepend validation
 */
export interface DeviceFieldValues {
  deviceId: string;
  notes?: string;
  annotatedAssetId?: string;
  annotatedUser?: string;
  annotatedLocation?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BulkEditValidationService {
  /**
   * Validate a value for Replace operation
   * @param fieldName The field being updated
   * @param value The new value
   * @returns Validation result with all applicable errors
   */
  validateReplaceValue(fieldName: string, value: string): ValidationResult {
    if (!value) {
      return { valid: true };
    }

    const errorMessages: string[] = [];

    // Check pattern for annotatedAssetId
    if (fieldName === 'annotatedAssetId' && ASSET_ID_INVALID_CHARS_PATTERN.test(value)) {
      errorMessages.push(ASSET_ID_PATTERN_ERROR_MESSAGE);
    }

    // Check character limit
    const maxLength = this.getFieldMaxLength(fieldName);
    if (maxLength && value.length > maxLength) {
      errorMessages.push(TEXT_LIMIT_EXCEEDED_ERROR_MESSAGE);
    }

    if (errorMessages.length > 0) {
      return {
        valid: false,
        errorMessage: errorMessages[0], // Keep first error for backwards compatibility
        errorMessages,
      };
    }

    return { valid: true };
  }

  /**
   * Validate a value for Append or Prepend operations
   * This considers the existing values on all selected devices
   * @param fieldName The field being updated
   * @param newValue The value to append/prepend
   * @param updateType The type of update (Append or Prepend)
   * @param deviceValues Array of current field values for selected devices
   * @returns Validation result with affected device count if invalid
   */
  validateAppendPrependValue(
    fieldName: string,
    newValue: string,
    updateType: UpdateType,
    deviceValues: DeviceFieldValues[]
  ): ValidationResult {
    if (!newValue) {
      return { valid: true };
    }

    const errorMessages: string[] = [];

    // Check pattern for annotatedAssetId (even for append/prepend)
    if (fieldName === 'annotatedAssetId' && ASSET_ID_INVALID_CHARS_PATTERN.test(newValue)) {
      errorMessages.push(ASSET_ID_PATTERN_ERROR_MESSAGE);
    }

    const maxLength = this.getFieldMaxLength(fieldName);

    if (maxLength && newValue.length > maxLength) {
      errorMessages.push(TEXT_LIMIT_EXCEEDED_ERROR_MESSAGE);
    }

    // Check resultant length for each device
    let affectedDeviceCount = 0;
    if (maxLength && newValue.length <= maxLength) {
      for (const device of deviceValues) {
        const currentValue = this.getDeviceFieldValue(device, fieldName) || '';
        const separatorLength = currentValue.length > 0 ? 2 : 0;
        const resultantLength = currentValue.length + separatorLength + newValue.length;

        if (resultantLength > maxLength) {
          affectedDeviceCount++;
        }
      }

      if (affectedDeviceCount > 0) {
        const operationName = updateType === UpdateType.APPEND ? 'appending' : 'prepending';
        errorMessages.push(
          `Text limit would be exceeded for ${affectedDeviceCount} device${affectedDeviceCount === 1 ? '' : 's'} after ${operationName}.`
        );
      }
    }

    if (errorMessages.length > 0) {
      return {
        valid: false,
        errorMessage: errorMessages[0],
        errorMessages,
        affectedDeviceCount: affectedDeviceCount > 0 ? affectedDeviceCount : undefined,
      };
    }

    return { valid: true };
  }

  /**
   * Get the maximum allowed input length for a field
   * For all update types: returns the field's max length
   * Note: For Append/Prepend, this returns the theoretical max, not the available space.
   * Use validateAppendPrependValue to check if the operation will succeed for all devices.
   * @param fieldName The field name
   * @param deviceValues Optional device values (not used, kept for API compatibility)
   * @param updateType The type of update (not used, kept for API compatibility)
   * @returns Maximum allowed characters for the field
   */
  getMaxAllowedInputLength(
    fieldName: string,
    _deviceValues?: DeviceFieldValues[],
    _updateType?: UpdateType
  ): number | undefined {
    return this.getFieldMaxLength(fieldName);
  }

  /**
   * Get the max length for a field from DEVICE_FIELD_LIMITS
   */
  private getFieldMaxLength(fieldName: string): number | undefined {
    // Handle both 'notes' and 'annotatedAssetId' style names
    const normalizedName = fieldName as DeviceFieldName;
    return DEVICE_FIELD_LIMITS[normalizedName];
  }

  /**
   * Get a field value from a device based on field name
   */
  private getDeviceFieldValue(device: DeviceFieldValues, fieldName: string): string | undefined {
    switch (fieldName) {
      case 'notes':
        return device.notes;
      case 'annotatedAssetId':
        return device.annotatedAssetId;
      case 'annotatedUser':
        return device.annotatedUser;
      case 'annotatedLocation':
        return device.annotatedLocation;
      default:
        return undefined;
    }
  }
}
